/**
 * Recebe tokens do Worker (fragment) e hidrata o cache do Auth0 SPA SDK.
 * Formato compatível com @auth0/auth0-spa-js @@auth0spajs@@.
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split('.')[1]
  if (!part) throw new Error('id_token inválido')
  const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
  return JSON.parse(atob(b64 + pad)) as Record<string, unknown>
}

function buildDecodedToken(idToken: string) {
  const claims = decodeJwtPayload(idToken)
  const user: Record<string, unknown> = { ...claims }
  for (const k of [
    'iss',
    'aud',
    'iat',
    'exp',
    'nbf',
    'nonce',
    'azp',
    'auth_time',
  ]) {
    delete user[k]
  }
  return {
    encoded: idToken,
    header: {},
    claims,
    user,
  }
}

export function hydrateAuth0SessionFromHash(clientId: string): boolean {
  const hash = window.location.hash.replace(/^#/, '')
  if (!hash) return false

  const params = new URLSearchParams(hash)
  const accessToken = params.get('access_token')
  const idToken = params.get('id_token')
  const refreshToken = params.get('refresh_token')
  const expiresIn = Number(params.get('expires_in') || '86400')
  const tokenType = params.get('token_type') || 'Bearer'

  if (!accessToken || !idToken) return false

  // A chave do cache tem que ser a mesma do Auth0Provider
  // (audience + scope com offline_access). Senão isAuthenticated=false
  // e o LoginGate manda de volta ao UL — loop pós-OTP.
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE || 'default'
  const scope = 'openid profile email offline_access'
  const decodedToken = buildDecodedToken(idToken)
  const nowSec = Math.floor(Date.now() / 1000)

  const body = {
    client_id: clientId,
    access_token: accessToken,
    id_token: idToken,
    refresh_token: refreshToken || undefined,
    expires_in: expiresIn,
    token_type: tokenType,
    scope,
    audience,
    decodedToken,
  }

  const cachePayload = JSON.stringify({ body, expiresAt: nowSec + expiresIn })
  const audiences = Array.from(new Set([audience, 'default']))
  for (const aud of audiences) {
    localStorage.setItem(
      `@@auth0spajs@@::${clientId}::${aud}::${scope}`,
      cachePayload,
    )
  }

  const idKey = `@@auth0spajs@@::${clientId}::@@user@@`
  localStorage.setItem(
    idKey,
    JSON.stringify({
      id_token: idToken,
      decodedToken,
    }),
  )

  // Cookie que o SDK usa para isAuthenticated rápido
  document.cookie = `auth0.${clientId}.is.authenticated=true; path=/; Secure; SameSite=Lax; Max-Age=${expiresIn}`

  // Limpa fragment da URL
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${window.location.search}`,
  )

  return true
}
